import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import { IoChevronDownOutline } from 'react-icons/io5';
import { MdDelete } from 'react-icons/md';
import { FaPlus } from 'react-icons/fa6';
import Button from 'components/Common/Button';
import Input from 'components/Common/Input';
import TimePicker from 'components/Common/TimePicker';
import FormSchemaParser from '@/components/Common/FormSchemaParser';

interface AdvancedMdFormProps {
  namePrefix: string;
  control: any;
  disabled?: boolean;
  configFields: any;
  spacing: string;
  submitForm?: any;
  publicForm?: boolean;
}

const fieldSx = {
  flex: 1,
  minWidth: 0,
  '& .MuiTextField-root, & .MuiPickersTextField-root, & .MuiFormControl-root': {
    width: '100%'
  }
};

const AdvancedMdForm = ({
  namePrefix,
  control,
  disabled,
  configFields,
  spacing,
  submitForm,
  publicForm
}: AdvancedMdFormProps) => {
  const fields = configFields || [];
  const blockOutIndex = fields.findIndex((field: any) => field.key === 'block_outs');
  const blockOutField = blockOutIndex >= 0 ? fields[blockOutIndex] : null;
  const beforeFields = blockOutIndex >= 0 ? fields.slice(0, blockOutIndex) : fields;
  const afterFields = blockOutIndex >= 0 ? fields.slice(blockOutIndex + 1) : [];

  return (
    <>
      {beforeFields.length > 0 && (
        <FormSchemaParser
          publicForm={publicForm}
          disabled={disabled}
          fields={beforeFields}
          control={control}
          namePrefix={namePrefix}
          gridSize={12}
          spacing={spacing}
          submitForm={submitForm}
        />
      )}

      {blockOutField && (
        <Box mt={beforeFields.length > 0 ? 2 : 0}>
          <BlockOutSection
            fieldConfig={blockOutField}
            namePrefix={namePrefix}
            control={control}
            disabled={disabled}
            publicForm={publicForm}
          />
        </Box>
      )}

      {afterFields.length > 0 && (
        <Box mt={2}>
          <FormSchemaParser
            publicForm={publicForm}
            disabled={disabled}
            fields={afterFields}
            control={control}
            namePrefix={namePrefix}
            gridSize={12}
            spacing={spacing}
            submitForm={submitForm}
          />
        </Box>
      )}
    </>
  );
};

const BlockOutSection = ({
  fieldConfig,
  namePrefix,
  control,
  disabled,
  publicForm
}: {
  fieldConfig: any;
  namePrefix: string;
  control: any;
  disabled?: boolean;
  publicForm?: boolean;
}) => {
  const fieldName = `${namePrefix}.${fieldConfig.key}`;
  const { fields, append, remove } = useFieldArray({ control, name: fieldName });
  const [expanded, setExpanded] = useState(false);
  const nameField = fieldConfig.group?.find((field: any) => field.key === 'name');
  const startField = fieldConfig.group?.find((field: any) => field.key === 'start_time');
  const endField = fieldConfig.group?.find((field: any) => field.key === 'end_time');

  useEffect(() => {
    if (fields.length > 0) setExpanded(true);
  }, [fields.length]);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disabled={disabled}
      sx={{
        boxShadow: 'none',
        overflow: 'auto',
        border: '1px solid #F3F3F3',
        '::before': { background: 'transparent' }
      }}
    >
      <AccordionSummary expandIcon={<IoChevronDownOutline style={{ fontSize: 22 }} />}>
        <Box>
          <Typography variant='h6' fontSize={16} fontWeight={600}>
            {fieldConfig.label || 'Block Out'}
            {fieldConfig.required && <span style={{ color: 'red' }}> *</span>}
          </Typography>
          {fieldConfig.description ? (
            <Typography fontSize={12} color='text.secondary' sx={{ pr: 2 }}>
              {fieldConfig.description}
            </Typography>
          ) : null}
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        {fields.map((field, index) => (
          <Box
            key={field.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mb: 2,
              flexWrap: 'wrap'
            }}
          >
            <Box sx={fieldSx}>
              <Input
                name={`${fieldName}.${index}.name`}
                label={nameField?.label || 'Block Out Name'}
                control={control}
                placeholder={nameField?.placeholder}
                required={Boolean(nameField?.required) && !publicForm}
                fullWidth
                disabled={disabled}
              />
            </Box>
            <Box sx={fieldSx}>
              <TimePicker
                name={`${fieldName}.${index}.start_time`}
                control={control}
                label={startField?.label || 'Start Time'}
                disabled={disabled}
                fullWidth
              />
            </Box>
            <Box sx={fieldSx}>
              <TimePicker
                name={`${fieldName}.${index}.end_time`}
                control={control}
                label={endField?.label || 'End Time'}
                disabled={disabled}
                fullWidth
              />
            </Box>
            {!disabled && (
              <IconButton color='error' onClick={() => remove(index)}>
                <MdDelete />
              </IconButton>
            )}
          </Box>
        ))}

        {!disabled && (
          <Button
            type='button'
            color='secondary'
            size='small'
            variant='outlined'
            startIcon={<FaPlus />}
            onClick={() => append({ name: '', start_time: '', end_time: '' })}
            sx={{ display: 'flex', margin: 'auto', marginTop: 1 }}
          >
            Add {fieldConfig.label || 'Block Out'}
          </Button>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default AdvancedMdForm;
